import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TourStep } from "./tourSteps";
import type { TourEndReason } from "./TourProvider";

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourSpotlightProps {
  step: TourStep;
  stepIndex: number;
  stepCount: number;
  onNext: () => void;
  onSkip: () => void;
  onEnd: (completed?: boolean, reason?: TourEndReason) => void;
  onSuggestion: (text: string) => void;
}

const HOLE_PADDING = 4; // px of breathing room revealed around the target
const TOOLTIP_GAP = 14; // px between the target and the coachmark
const TOOLTIP_WIDTH = 300;
const LEFT_PLACEMENT_GAP = 24; // px between a "left"-placed coachmark and the modal
const LEFT_TOP_OFFSET = 18; // px to nudge a "left"-placed coachmark above the modal top
const RESOLVE_TIMEOUT_MS = 1500;
const OPTIONAL_RESOLVE_TIMEOUT_MS = 500;
const TourSpotlight: React.FC<TourSpotlightProps> = ({
  step,
  stepIndex,
  stepCount,
  onNext,
  onSkip,
  onEnd,
  onSuggestion,
}) => {
  const [rect, setRect] = useState<SpotRect | null>(null);
  const [missing, setMissing] = useState(false);
  const resolvedRef = useRef(false);
  const targetElRef = useRef<HTMLElement | null>(null);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipHeight, setTooltipHeight] = useState(0);
  // Tracks the step we've already moved focus to, so focus lands once per step, not per frame.
  const focusedStepRef = useRef(-1);

  // Continuously glue the spotlight to the target (handles resize, scroll, nav
  // expand animations, route transitions). Polls until the target appears.
  useEffect(() => {
    resolvedRef.current = false;
    targetElRef.current = null;
    setRect(null);
    setMissing(false);
    const startedAt = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      // Reuse the cached node; only re-scan the DOM if it's missing or has detached
      // (e.g. a route change swapped it out).
      let el = targetElRef.current;
      if (!el || !el.isConnected) {
        el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
        targetElRef.current = el;
      }
      const r = el?.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        resolvedRef.current = true;
        // Only re-render when the measured rect actually moves — the rAF loop runs
        // every frame to stay glued, but most frames the target hasn't shifted.
        setRect((prev) =>
          prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
            ? prev
            : { top: r.top, left: r.left, width: r.width, height: r.height }
        );
      } else if (
        !resolvedRef.current &&
        now - startedAt >
          (step.optional ? OPTIONAL_RESOLVE_TIMEOUT_MS : RESOLVE_TIMEOUT_MS)
      ) {
        setMissing(true);
        return; // stop polling — the missing-target effect handles the transition
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step.target, step.optional]);

  // Advance when the user clicks the spotlighted target (capture phase so we still
  // catch it even when the click triggers navigation).
  useEffect(() => {
    if (step.advanceOn !== "click") return;
    const handler = (e: MouseEvent) => {
      if ((e.target as Element)?.closest(`[data-tour="${step.target}"]`)) onNext();
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [step.advanceOn, step.target, onNext]);

  // Missing target: skip optional steps, otherwise abort the tour (flag still set so it
  // doesn't get stuck replaying) — reported as target_missing, not a completion.
  useEffect(() => {
    if (!missing) return;
    if (step.optional) onNext();
    else onEnd(false, "target_missing");
  }, [missing, step.optional, onNext, onEnd]);

  // Escape ends the tour like the Skip button, so keyboard users are never trapped.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onSkip]);

  // Move focus into the coachmark once per step so screen-reader users land on the new copy.
  useEffect(() => {
    if (rect && focusedStepRef.current !== stepIndex) {
      focusedStepRef.current = stepIndex;
      tooltipRef.current?.focus();
    }
  }, [rect, stepIndex]);

  useLayoutEffect(() => {
    if (tooltipRef.current) setTooltipHeight(tooltipRef.current.offsetHeight);
  }, [step.title, step.body, rect]);

  const isLast = stepIndex >= stepCount - 1;
  const showNext = (step.advanceOn ?? "manual") === "manual";

  // While resolving (or about to skip), keep the app blurred so it never flashes.
  if (!rect) {
    return createPortal(
      <div className="fixed inset-0 z-[10000] backdrop-blur-sm bg-black/40" aria-hidden />,
      document.body
    );
  }

  const hole = {
    top: Math.max(0, rect.top - HOLE_PADDING),
    left: Math.max(0, rect.left - HOLE_PADDING),
    width: rect.width + HOLE_PADDING * 2,
    height: rect.height + HOLE_PADDING * 2,
  };
  const holeBottom = hole.top + hole.height;
  const holeRight = hole.left + hole.width;

  const panelClass = "fixed backdrop-blur-sm bg-black/40 z-[10000]";

 
  const place = step.placement ?? "bottom";
  const clampLeft = (l: number) =>
    Math.min(Math.max(8, l), Math.max(8, window.innerWidth - TOOLTIP_WIDTH - 8));
  const clampTop = (t: number) =>
    Math.min(Math.max(8, t), Math.max(8, window.innerHeight - tooltipHeight - 8));
  const tooltipStyle: React.CSSProperties = { maxWidth: TOOLTIP_WIDTH };
  if (place === "top") {
    tooltipStyle.top = clampTop(hole.top - TOOLTIP_GAP - tooltipHeight);
    tooltipStyle.left = clampLeft(hole.left);
  } else if (place === "right") {
    tooltipStyle.top = clampTop(hole.top);
    tooltipStyle.left = clampLeft(holeRight + TOOLTIP_GAP);
  } else if (place === "left") {
    tooltipStyle.top = clampTop(rect.top - LEFT_TOP_OFFSET);
    tooltipStyle.left = clampLeft(hole.left - LEFT_PLACEMENT_GAP - TOOLTIP_WIDTH);
  } else {
    tooltipStyle.top = clampTop(holeBottom + TOOLTIP_GAP);
    tooltipStyle.left = clampLeft(hole.left);
  }

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={step.title}>
      {/* Four blurred panels framing the transparent hole over the target.
          Anchored with right/bottom (not 100vw/100vh) to avoid scrollbar-width overflow. */}
      <div className={panelClass} style={{ top: 0, left: 0, right: 0, height: hole.top }} />
      <div className={panelClass} style={{ top: holeBottom, left: 0, right: 0, bottom: 0 }} />
      <div
        className={panelClass}
        style={{ top: hole.top, left: 0, width: hole.left, height: hole.height }}
      />
      <div
        className={panelClass}
        style={{ top: hole.top, left: holeRight, right: 0, height: hole.height }}
      />

      {/* Descriptive steps just explain the control — swallow clicks so it can't open a
          menu/modal behind the tour. Click/send steps stay interactive to advance. */}
      {showNext && (
        <div
          className="fixed z-[10000] cursor-default"
          style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
          onClick={(e) => e.stopPropagation()}
          aria-hidden
        />
      )}

      {/* Highlight ring around the hole (non-interactive so it never blocks the catcher) */}
      <div
        className="fixed z-[10000] pointer-events-none rounded-lg ring-2 ring-eai-primary-accent ring-offset-2 ring-offset-transparent"
        style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
        aria-hidden
      />

      {/* Coachmark — focused on each step change so screen readers announce the new copy. */}
      <div
        ref={tooltipRef}
        tabIndex={-1}
        className="fixed z-[10060] w-[calc(100vw-16px)] sm:w-[300px] rounded-lg bg-white p-4 shadow-xl border border-gray-200 outline-none"
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">{step.title}</h3>
          <span className="flex-shrink-0 text-xs font-medium text-gray-400">
            {stepIndex + 1}/{stepCount}
          </span>
        </div>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{step.body}</p>
        {/* Tap-to-fill starter prompt so the user never has to type to begin. */}
        {step.suggestion && (
          <button
            type="button"
            onClick={() => onSuggestion(step.suggestion as string)}
            className="mt-3 w-full rounded-lg border border-eai-primary-accent/40 bg-eai-primary-accent/5 px-3 py-2 text-left text-sm font-medium text-eai-primary-accent transition-colors hover:bg-eai-primary-accent/10 active:bg-eai-primary-accent/20"
          >
            {step.suggestion}
          </button>
        )}
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip tour
          </button>
          {showNext ? (
            <button
              type="button"
              onClick={onNext}
              className="eai-btn-grn px-4 py-2 text-sm"
            >
              {isLast ? "Got it" : "Next"}
            </button>
          ) : (
            <span className="text-xs font-medium text-eai-primary-accent">
              {step.advanceOn === "send" ? "Send a message to continue" : "Click the icon to continue"}
            </span>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TourSpotlight;
